import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { MdAdd, MdDelete } from "react-icons/md";
import User from "../../models/User";
import { useToast } from "../../context/ToastContext";
import { sendRequest } from "../../utils/api";
import { initAllStores } from "../../utils/cache";
import FullPageSpinner from "../../components/FullPageSpinner";
import Payslip from "./PaySlip";
import { getUser, initPayroll } from "../../utils/user-helper";
import { formatNaira } from "../../utils/bill";
import Payroll from "../../models/Payroll";
import { COMPANY_NAME } from "../../constants";
// import { FaEye, FaEyeSlash } from "react-icons/fa";
import Logo from "../../assets/logo.png";
import { capitalizeFirst } from "../../utils/string";


const cleanAmount = (value: string): number => {
  const cleaned = value.replace(/,/g, ""); // replace all commas
  return parseFloat(cleaned);
};

type PrintPayrollParams = {
  hotelName: string;
  dateStr: string;
  payrolls: Payroll[];
};

export function printPayroll({
  hotelName,
  dateStr,
  payrolls,
}: PrintPayrollParams) {


  // Split the string
const [yearStr, monthStr] = dateStr.split("-");

// Convert month number to integer (0-based for Date)
const monthIndex = parseInt(monthStr, 10) - 1; // 0 = January

// Month names array
const monthNames = [
  "January", "February", "March", "April",
  "May", "June", "July", "August",
  "September", "October", "November", "December"
];

const month = monthNames[monthIndex];
const year = parseInt(yearStr, 10);

  const totalEarnings = payrolls.reduce(
    (sum, p) => sum + (cleanAmount(p.totalEarnings) ?? 0),
    0
  );

  const totalDeductions = payrolls.reduce(
    (sum, p) => sum + (cleanAmount(p.totalDeductions) ?? 0),
    0
  );

  const totalNetPay = payrolls.reduce(
    (sum, p) => sum + (cleanAmount(p.netPay ?? 0)),
    0
  );

  const rows = payrolls
    .map(
      (p, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${p.user.firstName} ${p.user.lastName}</td>
        <td>${p.user.portfolio ?? ""}</td>
        <td>${p.user.accountNo ?? ""}</td>
        <td>${(p.totalEarnings ?? 0).toLocaleString()}</td>
        <td>${(p.totalDeductions ?? 0).toLocaleString()}</td>
        <td>${(p.netPay ?? 0).toLocaleString()}</td>
      </tr>
    `
    )
    .join("");

  const html = `
  <html>
  <head>
    <title>Staff Payroll - ${month} ${year}</title>
    <style>
      body{
        font-family: Arial, sans-serif;
        padding:40px;
      }

      .header{
        text-align:center;
        margin-bottom:30px;
      }

      .hotel{
        font-size:22px;
        font-weight:bold;
      }

      .title{
        font-size:18px;
        margin-top:5px;
      }

      table{
        width:100%;
        border-collapse:collapse;
        margin-top:20px;
      }

      th, td{
        border:1px solid #000;
        padding:8px;
        text-align:left;
      }

      th{
        background:#f2f2f2;
      }

      .total-row{
        font-weight:bold;
        background:#f9f9f9;
        text-align: center;
      }

      .signature{
        margin-top:80px;
        display:flex;
        justify-content:space-between;
      }

      .line{
        margin-top:40px;
        border-top:1px solid black;
        width:200px;
      }

      @media print {
        body{
          margin:0;
        }
      }
    </style>
  </head>

  <body>

  <div class="header">
    <div class="hotel">${hotelName}</div>
    <div class="title">Staff Payroll - ${month} ${year}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>S/N</th>
        <th>Staff Name</th>
        <th>Position</th>
        <th>Account</th>
        <th>Earnings</th>
        <th>Deductions</th>
        <th>Net Pay</th>
      </tr>
    </thead>

    <tbody>
      ${rows}

      <tr class="total-row">
        <td colspan="4" style="text-align:center;">TOTAL</td>
        <td>₦${totalEarnings.toLocaleString()}</td>
        <td>₦${totalDeductions.toLocaleString()}</td>
        <td>₦${totalNetPay.toLocaleString()}</td>
      </tr>

    </tbody>
  </table>

  <div class="signature">
    <div>
      <div class="line"></div>
      <p style="font-weight: bold;">Prepared By:</p>
    </div>

    <div>
      <div class="line"></div>
      <p style="font-weight: bold;">Approved By:</p>
    </div>
  </div>

  </body>
  </html>
  `;

  const win = window.open("", "_blank");

  if (!win) return;

  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}


export default function PayrollModal({
  staff,
  setStaffs,
  onClose,
}: {
  staff: User;
  setStaffs: React.Dispatch<React.SetStateAction<User[]>>;
  onClose: () => void;
}) {

  // console.log(staff)
  const [showPayslip, setShowPayslip] = useState(false);

  const staffEarnings = staff.payroll?.earnings;
  const staffDeductions = staff.payroll?.deductions;
  const [company, setCompany] = useState<{ name: string, logo: string } | null>(null);

  const [earnings, setEarnings] = useState<PayRoll[]>(initPayroll(staff, "earn", staffEarnings));
  const [deductions, setDeductions] = useState<PayRoll[]>(initPayroll(staff, "deduct", staffDeductions));

  const { showToast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Fetch company data on mount
    const fetchCompany = async () => {
      const data = await getUser();
      if (data) {
        setCompany({
          name: COMPANY_NAME,
          logo: Logo,
        });
      }
    };

    fetchCompany();
  }, []);

  const totalEarnings = earnings.reduce((a, b) => a + Number(b.amount || 0), 0);
  const totalDeductions = deductions.reduce((a, b) => a + Number(b.amount || 0), 0);
  const netPay = totalEarnings - totalDeductions;

  const addItem = (type: "earning" | "deduction") => {
    if (type === "earning") setEarnings([...earnings, { title: "", amount: "" }]);
    else setDeductions([...deductions, { title: "", amount: "" }]);
  };

  
  const updateItem = (
    type: "earning" | "deduction",
    index: number,
    field: "title" | "amount",
    value: string
  ) => {
    const list = type === "earning" ? [...earnings] : [...deductions];

    if (field === "title") {
      const normalized = value.trim().toLowerCase();

      // Prevent empty title
      if (normalized === "") {
        list[index].title = "";
      } else {
        // Check for duplicates (ignore current index)
        const isDuplicate = list.some((item, i) => i !== index && item.title.toLowerCase() === normalized);
        if (isDuplicate) {
          showToast(
            `${type === "earning" ? "Earning" : "Deduction"} titled '${value}' already exists.`,
            { type: "error" }
          );
          return;
        }
        list[index].title = value;
      }
    } else if (field === "amount") {
      const sanitized = value.replace(/^0+(?=\d)/, '');
      list[index].amount = sanitized;
    }

    type === "earning" ? setEarnings(list) : setDeductions(list);
  };

  const removeItem = (type: "earning" | "deduction", index: number) => {
    const list = type === "earning" ? earnings : deductions;
    const item = list[index];

    // Prevent removing Basic Salary
    const title = item?.title.toLocaleLowerCase();
    if (title === "basic salary" || title === "transportation" || title === "housing") {
      showToast(`${capitalizeFirst(title)} cannot be removed`);
      return;
    } 

    if (type === "earning") {
      setEarnings(earnings.filter((_, i) => i !== index));
    } else {
      setDeductions(deductions.filter((_, i) => i !== index));
    }
  };

  async function savePayslip(
    staff: User,
    earnings: { title: string; amount: string }[],
    deductions: { title: string; amount: string }[]
  ) {
    setLoading(true);

    try {
      // Validation: all titles and amounts must be filled
      const invalidEarnings = earnings.some(e => !e.title.trim() || !e.amount.trim());
      const invalidDeductions = deductions.some(d => !d.title.trim() || !d.amount.trim());

      if (invalidEarnings || invalidDeductions) {
        showToast("All earnings and deductions must have a title and amount.", { type: "error" });
        return;
      }

      const cache = await initAllStores();

      const sanitized = (title: string) => {
        return title.trim().toLocaleLowerCase();
      }
      // Prepare payload
      const payload = {
        staffId: staff.id,
        earnings: earnings.map(e => ({ title: sanitized(e.title), amount: Number(e.amount) })),
        deductions: deductions.map(d => ({ title: sanitized(d.title), amount: Number(d.amount) })),
      };

      // Send request & update cache
      await Promise.all([
        sendRequest("POST", `/users/${staff.id}/payroll`, payload),
        cache.updateManyBy("staffs", [staff.id], { payroll: payload })
      ]);

      const formatted = {
        earnings: Object.fromEntries(
          earnings.map(e => [sanitized(e.title), Number(e.amount)])
        ),
        deductions: Object.fromEntries(
          deductions.map(d => [sanitized(d.title), Number(d.amount)])
        )
      };

      // console.log("Formatted Payroll:", formatted);
      setStaffs(prev => prev.map(
        st => st.id === staff.id
          ? { ...st, payroll: formatted } : st
      ));
      showToast("Payslip saved successfully.", { type: "success" });

    } catch (err: any) {
      showToast(
        "Failed to save payslip. Please check your network and try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[75vh] overflow-hidden animate-fadeIn">
        <FullPageSpinner show={loading} />

        {showPayslip && (
          <Payslip
            onClose={() => setShowPayslip(false)}
            employee={staff}
            company={company as { name: string, logo: string }}
            earnings={earnings}
            deductions={deductions}
          />
        )}

        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Manage Payroll – {staff.firstName} {staff.lastName}
              {/* {staff.accountNo ? `(${staff.accountNo})` : ""} */}
            </h2>
            <p className="text-sm text-gray-500">{staff.portfolio}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            {FaTimes({ className: "text-gray-600 text-lg" })}
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto px-6 py-4 space-y-6">
          {/* Earnings Section */}
          <section>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-green-700">Earnings</h3>
              <button
                onClick={() => addItem("earning")}
                className="text-sm text-green-600 flex items-center gap-1 hover:text-green-700"
              >
                {MdAdd({})} Add Earning
              </button>
            </div>
            {earnings.map((item, i) => (
              <div key={i} className="flex items-center gap-3 mb-2">
                <input
                  type="text"
                  placeholder="Title (e.g. Basic, Allowance)"
                  value={item.title}
                  disabled={
                    item.title.toLowerCase() === "basic salary" ||
                    item.title.toLowerCase() === "transportation" ||
                    item.title.toLowerCase() === "housing"
                  }
                  onChange={(e) => updateItem("earning", i, "title", e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm capitalize"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={item.amount}
                  onChange={(e) => updateItem("earning", i, "amount", e.target.value)}
                  className="w-32 border rounded-lg px-3 py-2 text-sm"
                />
                <button onClick={() => removeItem("earning", i)} className="text-red-500">
                  {MdDelete({})}
                </button>
              </div>
            ))}
          </section>

          {/* Deductions Section */}
          <section>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-red-700">Deductions</h3>
              <button
                onClick={() => addItem("deduction")}
                className="text-sm text-red-600 flex items-center gap-1 hover:text-red-700"
              >
                {MdAdd({})} Add Deduction
              </button>
            </div>
            {deductions.map((item, i) => (
              <div key={i} className="flex items-center gap-3 mb-2">
                <input
                  type="text"
                  placeholder="Title (e.g. Loan, Tax)"
                  value={item.title}
                  onChange={(e) => updateItem("deduction", i, "title", e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm capitalize"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={item.amount}
                  onChange={(e) => updateItem("deduction", i, "amount", e.target.value)}
                  className="w-32 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  onClick={() => removeItem("deduction", i)}
                  className="text-red-500"
                // disabled={item.title.toLocaleLowerCase() === "basic salary"}
                >
                  {MdDelete({})}
                </button>
              </div>
            ))}
          </section>

          {/* Totals */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Total Earnings:</span>
              <span className="font-medium text-green-700">₦{formatNaira(totalEarnings)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Total Deductions:</span>
              <span className="font-medium text-red-700">₦{formatNaira(totalDeductions)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold border-t pt-2">
              <span>Net Pay:</span>
              <span className="text-blue-700">₦{formatNaira(netPay)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between px-6 py-4 border-t sticky bottom-0 bg-white z-10 rounded-b-2xl">
          {/* Left side */}
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>

          {/* Right side */}
          <div className="flex gap-3">
            <button
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
              onClick={() => setShowPayslip(true)}
            >
              View Slip
            </button>

            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
              onClick={() => savePayslip(staff, earnings, deductions)}
            >
              Save
            </button>
          </div>

        </div>

      </div>
    </div>
  );
}
